<!-- packages/studio/src/components/army/WarscrollPreview.vue -->
<!-- Mirrors the frontend warscroll (StatsBar + WeaponTable + KeywordsBar +
     AbilityCard), trimmed to the data an authored unit carries, so the editor
     preview matches what the app renders. -->
<template>
  <div class="warscroll">
    <header class="warscroll__head">
      <h3 class="warscroll__name">{{ unit.name || '(unnamed unit)' }}</h3>
      <div class="warscroll__badges">
        <span v-if="unit.points" class="warscroll__badge warscroll__badge--points">
          {{ unit.points }} pts
        </span>
        <span v-if="unit.legends" class="warscroll__badge warscroll__badge--legends">Legends</span>
      </div>
    </header>

    <div class="stats-row">
      <div v-for="s in statCircles" :key="s.label" class="stat">
        <span class="stat__label">{{ s.label }}</span>
        <div class="stat__container">
          <div class="stat__circle">
            <span class="stat__value">{{ s.value }}</span>
          </div>
          <div v-if="s.ward" class="stat__ward-pip">{{ s.ward }}</div>
        </div>
      </div>
    </div>

    <!-- Ranged and melee weapons are shown as separate tables (ranged first); a
         table is omitted entirely when the unit has none of that kind. -->
    <div v-for="group in weaponTables" :key="group.key" class="weapon-group">
      <div class="weapon-group__title">{{ group.label }}</div>
      <table class="weapons">
        <thead>
          <tr>
            <th v-for="h in tableHeaders(group.showRange)" :key="h" :class="cellClass(h)">
              {{ h }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="w in group.weapons" :key="w.id">
            <td class="name">
              <div>{{ w.name }}</div>
              <div v-if="visibleAbilities(w).length" class="weapon-abilities">
                {{ visibleAbilities(w).join(', ') }}
              </div>
            </td>
            <td v-if="group.showRange" class="range">{{ w.range || '–' }}</td>
            <td class="num">{{ dash(w.attack) }}</td>
            <td class="num">{{ dash(w.hit) }}</td>
            <td class="num">{{ dash(w.wound) }}</td>
            <td class="num">{{ dash(w.rend) }}</td>
            <td class="num">{{ dash(w.damage) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="abilities">
      <AbilityCard v-for="a in unit.abilities" :key="a.id" :ability="a" />
    </div>

    <div v-if="detail.length" class="detail">
      <span v-for="d in detail" :key="d" class="detail__item">{{ d }}</span>
    </div>

    <section v-if="optionSetsWithOptions.length" class="unit-details">
      <div v-for="set in optionSetsWithOptions" :key="set.name" class="unit-details__section">
        <span><i>This unit has 1 of the following options:</i></span>
        <ul class="unit-details__list">
          <li v-for="option in set.options" :key="option.name" v-html="formatText(option.name)" />
        </ul>
      </div>
    </section>

    <div v-if="allKeywords.length" class="keywords">
      <span class="keywords__label">Keywords</span>
      <ul class="keywords__list">
        <li v-for="kw in allKeywords" :key="kw" class="keywords__chip">{{ kw }}</li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IUnit, IWeapon, GrandAlliance } from '@scrolldex/shared';
import { unitWeapons, UnitType, WeaponType } from '@scrolldex/shared';
import AbilityCard from '../AbilityCard.vue';
import { derivedUnitKeywords } from '../../utils/unitKeywords.js';
import { formatText } from '../../utils/formatter.js';

const props = defineProps<{ unit: IUnit; armyKeyword?: string; alliance?: GrandAlliance }>();

// Unit types that cannot be reinforced by default — so the note is only worth
// showing when a unit deviates from its type's default (matches the app).
const REINFORCE_DEFAULT_NONE = new Set<UnitType>([
  UnitType.Hero,
  UnitType.Monster,
  UnitType.WarMachine,
  UnitType.Manifestation,
  UnitType.Terrain,
]);

// The detail line: points, unit size, base size, and a reinforcement note when
// it deviates from the unit type's default.
const detail = computed<string[]>(() => {
  const u = props.unit;
  const out: string[] = [];
  if (u.points) out.push(`${u.points} pts`);
  const size = u.models[0]?.min;
  if (size) out.push(`Unit size: ${size}`);
  const base = u.models[0]?.baseSize;
  if (base) out.push(`Base: ${base}`);
  const defaultNone = REINFORCE_DEFAULT_NONE.has(u.type);
  if (defaultNone && u.canBeReinforced) out.push('Can be reinforced');
  else if (!defaultNone && !u.canBeReinforced) out.push('Cannot be reinforced');
  return out;
});

const weapons = computed<IWeapon[]>(() => unitWeapons(props.unit));
// Ranged and melee weapons get their own tables, ranged first; a table is dropped
// when the unit has none of that kind. Only the ranged table carries a Range column.
const weaponTables = computed(() => {
  const groups: { key: string; label: string; weapons: IWeapon[]; showRange: boolean }[] = [];
  const ranged = weapons.value.filter((w) => w.type === WeaponType.Ranged);
  const melee = weapons.value.filter((w) => w.type === WeaponType.Melee);
  if (ranged.length)
    groups.push({ key: 'ranged', label: 'Ranged Weapons', weapons: ranged, showRange: true });
  if (melee.length)
    groups.push({ key: 'melee', label: 'Melee Weapons', weapons: melee, showRange: false });
  return groups;
});
const optionSetsWithOptions = computed(() =>
  props.unit.optionSets.filter((set) => set.options.length),
);

// App order (StatsBar): Move, Health, Control, Banishment, Save (with ward pip).
// A bare Ward with no Save shows as its own circle.
const statCircles = computed(() => {
  const u = props.unit;
  const out: { label: string; value: string; ward?: string }[] = [];
  if (u.move) out.push({ label: 'Move', value: u.move });
  if (u.health) out.push({ label: 'Health', value: u.health });
  if (u.control) out.push({ label: 'Control', value: u.control });
  if (u.banishment) out.push({ label: 'Banishment', value: u.banishment });
  if (u.save) out.push({ label: 'Save', value: u.save, ward: u.ward ?? undefined });
  else if (u.ward) out.push({ label: 'Ward', value: u.ward });
  return out;
});

function tableHeaders(showRange: boolean): string[] {
  return showRange
    ? ['Name', 'Range', 'Attacks', 'Hit', 'Wound', 'Rend', 'Damage']
    : ['Name', 'Attacks', 'Hit', 'Wound', 'Rend', 'Damage'];
}

const allKeywords = computed<string[]>(() => {
  const derived = derivedUnitKeywords(props.unit, {
    armyKeyword: props.armyKeyword,
    alliance: props.alliance,
  });
  const seen = new Set<string>();
  const out: string[] = [];
  for (const kw of [...derived, ...props.unit.keywords]) {
    const key = kw.toUpperCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(kw);
    }
  }
  return out;
});

function visibleAbilities(weapon: IWeapon): string[] {
  return weapon.abilities.filter((a) => a && a !== '-');
}
function dash(v: string): string {
  return v && v !== '' ? v : '–';
}
function cellClass(header: string): string {
  if (header === 'Name') return 'name';
  if (header === 'Range') return 'range';
  return 'num';
}
</script>

<style scoped>
.warscroll {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.warscroll__head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--st-space-1);
}
.warscroll__name {
  font-size: var(--st-text-lg);
  font-weight: var(--st-weight-bold);
  text-align: center;
}
.warscroll__badges {
  display: flex;
  gap: var(--st-space-1);
}
.warscroll__badge {
  padding: 2px var(--st-space-2);
  border-radius: var(--st-radius-full);
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-bold);
  white-space: nowrap;
}
.warscroll__badge--points {
  background: var(--badge-red);
  color: var(--badge-red-text);
}
.warscroll__badge--legends {
  background: var(--badge-purple);
  color: var(--badge-purple-text);
}

/* Detail line: points / unit size / base size / reinforcement. */
.detail {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-1) var(--st-space-3);
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  border-top: 1px solid var(--st-border);
  padding-top: var(--st-space-2);
}

/* Stat circles — mirrors StatCircle.vue, scaled for the editor pane. */
.stats-row {
  display: flex;
  gap: var(--st-space-5);
  justify-content: center;
  flex-wrap: wrap;
}
.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.stat__label {
  font-size: var(--st-text-xs);
  color: var(--st-text);
  margin-bottom: 0.2rem;
}
.stat__container {
  position: relative;
  width: 52px;
  height: 52px;
}
.stat__circle {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--bg-primary);
  border: 1px solid var(--st-border-strong);
  box-shadow: var(--st-shadow-1);
  display: flex;
  align-items: center;
  justify-content: center;
}
.stat__value {
  font-size: var(--st-text-lg);
  font-weight: var(--st-weight-bold);
  color: var(--st-text);
  line-height: 1;
}
.stat__ward-pip {
  position: absolute;
  top: 0;
  right: -0.6em;
  width: 1.8em;
  height: 1.8em;
  border-radius: 50%;
  background: var(--color-red);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--st-weight-bold);
  font-size: var(--st-text-xs);
  border: 2px solid var(--st-surface);
}

/* Weapon table — mirrors WeaponTable.vue. */
.weapon-group + .weapon-group {
  margin-top: var(--st-space-3);
}
.weapon-group__title {
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--st-text-muted);
  margin-bottom: var(--st-space-1);
}
.weapons {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--st-text-sm);
  background: var(--st-surface);
  box-shadow: var(--st-shadow-1);
}
.weapons th {
  background: var(--table-stripe);
  text-align: left;
  padding: var(--st-space-1) var(--st-space-2);
}
.weapons td {
  padding: var(--st-space-1) var(--st-space-2);
}
.weapons tbody tr:nth-child(even) {
  background: var(--table-stripe);
}
.weapons th.num,
.weapons td.num,
.weapons th.range,
.weapons td.range {
  text-align: center;
  width: 3.5em;
}
.weapon-abilities {
  font-size: var(--st-text-xs);
  color: var(--st-text-muted);
  margin-top: 2px;
}

.abilities {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}

.unit-details {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
  font-size: var(--st-text-sm);
  color: var(--st-text);
}
.unit-details__list {
  margin: var(--st-space-1) 0 0 var(--st-space-4);
  padding: 0;
}

.keywords {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-1);
}
.keywords__label {
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--st-text-muted);
}
.keywords__list {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-1);
}
.keywords__chip {
  padding: 2px var(--st-space-2);
  background: var(--st-surface-sunken);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-full);
  font-size: var(--st-text-xs);
  font-variant: small-caps;
  color: var(--st-text);
}
</style>
